/* ---------------------------------------------------------------------------
   Los CINCO estados de una consulta, en un solo lugar.

   Las pantallas venían decidiendo qué mostrar con `isError` / `isLoading` /
   `data`, y esa terna deja dos agujeros que hacen mentir a la interfaz:

   1. Sin red, la petición queda PAUSADA (`isPaused`): no está cargando, no hay
      error y no hay datos, así que ganaba la rama del vacío y el listado
      afirmaba "todavía no hay perfiles" con el backend lleno.

   2. Al reintentar SIN datos, TanStack Query resetea el estado a `pending`
      (ver `fetchState`: con `data === undefined` pone `error: null,
      status: 'pending'`). El card de error desaparecía en el mismo clic y lo
      reemplazaban los esqueletos, así que el botón que el usuario acababa de
      tocar nunca alcanzaba a decir que estaba trabajando.

   3. Una consulta que CONSERVA el resultado anterior mientras trae el nuevo
      (`placeholderData`) no enciende ninguno de los tres: hay datos, no hay
      error y `isLoading` es false. La pantalla se queda muda mostrando la
      respuesta a una pregunta que ya nadie hizo. Ese es `refrescando`.

   Es una función pura para poder probar esos casos sin montar pantallas.
--------------------------------------------------------------------------- */

export interface EstadoDeConsulta {
  /** Falló y no hay nada para mostrar (incluye el reintento en curso). */
  hayError: boolean;
  /** Pausada por falta de red: ni cargando, ni error, ni datos. */
  sinConexion: boolean;
  /** Primera carga en vuelo: corresponden los esqueletos. */
  cargando: boolean;
  /** Hay una petición en vuelo: el botón de reintento tiene que decirlo. */
  recargando: boolean;
  /**
   * Hay datos EN PANTALLA y una petición en vuelo.
   *
   * Se separa de `recargando` porque responde otra pregunta: aquel dice si el
   * botón que se acaba de tocar está trabajando; este, si lo que el usuario
   * está MIRANDO todavía corresponde a lo que pidió. Con `placeholderData` no
   * corresponde, y nada más en la interfaz lo delata.
   */
  refrescando: boolean;
}

interface ConsultaObservada<T> {
  isError: boolean;
  isPaused: boolean;
  isLoading: boolean;
  isFetching: boolean;
  errorUpdateCount: number;
  data: T | undefined;
}

export function estadoDeConsulta<T>(query: ConsultaObservada<T>): EstadoDeConsulta {
  const hayDatos = query.data !== undefined;
  const reintentandoSinDatos = !hayDatos && query.errorUpdateCount > 0 && query.isFetching;

  return {
    hayError: query.isError || reintentandoSinDatos,
    sinConexion: query.isPaused && !hayDatos,
    // El reintento se muestra en el card de error, no como primera carga.
    cargando: query.isLoading && !reintentandoSinDatos,
    recargando: query.isFetching,
    // `!isLoading` es lo que deja afuera la primera carga: ahí los esqueletos
    // ya lo dicen, y una franja de "actualizando" encima afirmaría que debajo
    // hay un resultado anterior que se está reemplazando.
    refrescando: hayDatos && query.isFetching && !query.isLoading,
  };
}

/** Combina dos consultas que alimentan la misma pantalla (la peor manda). */
export function combinarEstados(a: EstadoDeConsulta, b: EstadoDeConsulta): EstadoDeConsulta {
  return {
    hayError: a.hayError || b.hayError,
    sinConexion: a.sinConexion || b.sinConexion,
    cargando: a.cargando || b.cargando,
    recargando: a.recargando || b.recargando,
    refrescando: a.refrescando || b.refrescando,
  };
}
